/**
 * 웹에서 지원하는 라우트 패턴 (single source of truth).
 * - checkWebPageExists: URL 이동 전 페이지 존재 여부 확인
 * - 각 패키지 Navigator: 여기서 path를 import하여 ScreenItem에 사용
 *
 * 새 페이지 추가 시:
 * 1. 여기에 path 추가
 * 2. 해당 패키지 Navigator.tsx에서 import하여 ScreenItem 추가
 */
export const WEB_ROUTE_PATHS = {
  Home: '/',
  Login: '/login',
  EmailLogin: '/login/email',
  AuthCallback: '/auth/:provider',
  AlreadyRegistered: '/login/already-registered',
  RecoveryId: '/recovery/id',
  RecoveryPassword: '/recovery/password',
  GoodsDetail: '/goods/:goodsId',
  GoodsList: '/goods-list',
  // 오늘출발관. `configs.today_delivery.department_deeplink` 가 여는 지면이다.
  // (도착보장 하단탭 `/today_delivery` 는 같은 웹 경로에 `tab=third_pl` 을 붙인 다른 탭)
  TodayDelivery: '/today-delivery',
  GoodsReviewSno: '/reviews/:goodsSno',
  // 상품 상세에서 진입하는 리뷰 목록. `useMoveToReviewList` 가 만드는 경로와 같은 형태다.
  // (`/goods/:goodsSno/reviews/photo` 는 네이티브 전용이라 NATIVE_ONLY_WEBVIEW_PATTERNS 에 있다)
  GoodsReviewList: '/goods/:goodsSno/reviews',
  PlaceGoodsDetail: '/place/goods/:sno',
  // 온보딩(`screenName: TODAY_ONBOARDING`). 정적 경로다(`/onboarding` 은 404).
  // ⚠️ 전체화면 온보딩이라 페이지가 타이틀바를 그리지 않고 **뒤로가기도 막는다**
  // (`window.onBackbuttonPress = () => false`). 네이티브 헤더도 띄우지 않는다.
  TodayOnboarding: '/onboarding/today',
  // 플레이스 지도 / 검색. 둘 다 정적 경로다(`/place/map/1` 은 404).
  // 페이지가 자체 타이틀바를 그린다("플레이스 지도" / "검색").
  // 플레이스 카테고리 상세(목록). 콘텐츠 라우트는 `place/list/[category_sno]` 이고
  // `?sorting_type=` 같은 정렬 쿼리는 path 가 아니라 쿼리로 승계된다.
  // 예약 목록(`/place/reservation/list/:sno`)과는 세그먼트 구성이 달라 충돌하지 않는다.
  PlaceCategoryList: '/place/list/:categorySno',
  PlaceMap: '/place/map',
  PlaceSearch: '/place/search',
  PlaceReservationList: '/place/reservation/list/:sno',
  Cart: '/cart',
  // 장바구니에서 쿠폰을 고르는 화면. `/cart` 와 세그먼트 수가 달라 충돌하지 않는다.
  CartSelectCoupon: '/cart/select-coupon',
  PaymentCheckout: '/payment/checkout/:checkoutKey',
  // 결제 결과. 웹 페이지가 catch-all(`pages/payment/success/[...slug]`)이라 세그먼트 깊이가
  // 가변이다 — PG 가 돌려보내는 실제 경로는 레거시 엔드포인트 모양을 그대로 문다
  // (`/payment/success/m2/ord/complete.php?status=success&sno=...`).
  // 그래서 남은 경로 전체를 한 param 으로 받는다(`:slug(.*)` — PATH_PARAM_PATTERN 주석 참고).
  // 주문 식별자는 path 가 아니라 `?sno=` 쿼리이며 웹뷰 URL 로 그대로 승계된다.
  PaymentSuccess: '/payment/success/:slug(.*)',
  PaymentFail: '/payment/fail/:slug(.*)',
  OrderList: '/orders',
  OrderDetail: '/orders/:orderSno',
  // 상태별 주문 목록. `status` 는 `delivering` | `delivered` | `cancel` 이며 그 외 값은 콘텐츠가
  // `delivering` 으로 폴백한다. 세그먼트가 3단이라 주문 상세(2단)와 충돌하지 않는다
  // (`/orders/status` 자체는 orderSno='status' 인 주문 상세로 해석된다 — 웹도 그렇게 응답한다).
  OrderStatus: '/orders/status/:status',
  OrderCancellations: '/orders/:orderSno/cancellations',
  OrderCancellationsDetail: '/orders/:orderSno/cancellations/:cancellationId',
  OrderExchange: '/orders/:orderSno/exchange',
  // 교환요청 안의 마켓(판매자) 교환 흐름. `/exchange` 와 세그먼트 수가 달라 충돌하지 않는다.
  // (`/exchange/sellers/1` 은 404 = 정적 경로다)
  OrderExchangeSellers: '/orders/:orderSno/exchange/sellers',
  OrderReturn: '/orders/:orderSno/return',
  // 반품 상세. 목록(`/return`, 반품요청)과 세그먼트 수가 달라 충돌하지 않는다.
  // param 이름은 콘텐츠 라우트(`pages/orders/[sno]/return/[orderReturnSno]`)를 따랐다.
  OrderReturnDetail: '/orders/:orderSno/return/:orderReturnSno',
  OrderSalesInvoice: '/orders/:orderSno/sales-invoice',
  // 마지막 세그먼트는 배송 건 식별자. 웹 라우트가 2단 동적 세그먼트다.
  OrderDeliveryPost: '/orders/:orderSno/delivery-post/:sno',
  // 주문 아이템 단위 배송조회. `delivery-post` 하위에 `order-items` 라는 정적 세그먼트가 한 단
  // 더 붙어 5단이 되므로 위 4단 패턴과 충돌하지 않는다.
  // param 이름은 콘텐츠 라우트(`delivery-post/order-items/[order_item_sno]`)를 따랐다.
  OrderDeliveryPostOrderItem:
    '/orders/:orderSno/delivery-post/order-items/:orderItemSno',
  // 쿠폰/이벤트 목록. 상세(`/events/:eventId`)와 세그먼트 수가 달라 충돌하지 않는다.
  EventList: '/events',
  EventDetail: '/events/:eventId',
  // 기획전 안의 세그먼트(구좌) 지면. 상세(`/events/:eventId`)와 세그먼트 수가 달라 충돌하지 않는다.
  // param 이름은 콘텐츠 라우트(`pages/events/[sno]/segments/[segmentSno]`)를 따랐다.
  // `?segment_sno_list=` 는 path 가 아니라 쿼리라 웹뷰 URL 로 그대로 승계된다.
  EventSegmentDetail: '/events/:eventId/segments/:segmentSno',
  // 어필리에이트(파트너스) 기획전 상세. 일반 기획전(`/events/:eventId`)과 다른 지면이다.
  // ⚠️ stag-4 에는 배포돼 있지 않다(404). prod 만 200 — 스테이징 확인이 안 되는 경로다.
  AffiliateEventDetail: '/affiliate/events/:eventSno',
  // 매거진(스타일). 정적 경로다(`/style/1` 은 404). `WEB_EMBEDDED_WEBVIEW_PATH_PREFIXES` 에
  // 이미 `/style` 이 있어 하위 경로도 앱 안에서 열린다.
  Style: '/style',
  // 커뮤니티(톡) 홈. 페이지가 자체 타이틀바("커뮤니티")를 그린다.
  CommunityTalkHome: '/community/talk/home',
  // 커뮤니티의 옛 경로. 서버가 307 로 `/community/talk/home` 으로 보낸다(stag·prod 동일).
  // 이미 배포된 딥링크(`ably://webview?url=…/fun/talk`)가 이 경로를 물고 있어 함께 등록한다.
  // 등록하지 않으면 웹에서 전용 화면으로 못 가고 새 탭(window.open)으로 빠진다.
  FunTalk: '/fun/talk',
  // AI 채팅. 페이지가 자체 타이틀바("AI 채팅")를 그린다.
  ChatBot: '/chat-bot',
  // 운세 홈. 페이지가 자체 타이틀바("운세")를 그린다.
  FortuneHome: '/fortune/home',
  // 운세의 옛 경로. 서버가 308 두 번으로 `/fortune/home` 에 착지시킨다
  // (`/life/fortune/daily/` → `/life/fortune/daily` → `/fortune/home`).
  // `/fun/talk` 과 같은 이유로 등록한다 — 이미 배포된 딥링크가 이 경로를 물고 있다.
  LifeFortuneDaily: '/life/fortune/daily',
  // ⚠️ 아래 둘은 stag-4 에 배포돼 있지 않다(404). prod 만 200 이라 스테이징 확인이 안 되고,
  // ably-webview-react 에도 해당 페이지가 없어 다른 오리진이 서빙한다.
  ContentStyleHome: '/content/style/home',
  NovelHome: '/novel/home',
  MarketHome: '/markets/:marketId',
  // 마켓 정보는 웹 경로가 단수형(`/market/...`)이다. MarketHome(`/markets/...`)과 다름.
  MarketInfo: '/market/:sno/info',
  MumulLiveProfiles: '/mumul-live/profiles',
  // 무물라이브 LIVE 채널(프로필 상세). 세그먼트가 붙은 이 경로만 웹에 존재한다.
  MumulLiveProfileSno: '/mumul-live/profiles/:sno',
  AlarmSetting: '/mypage/alarm-setting',
  Laboratory: '/mypage/laboratory',
  PointOfContact: '/mypage/point-of-contact',
  MyPersonalization: '/mypage/personalization',
  Setting: '/mypage/setting',
  MyInfo: '/mypage/my-info',
  MyProfile: '/mypage/profile',
  // 프로필 하위 개인정보 수정 화면들. 서버가 각 경로에 실제 라우트를 갖고 있다
  // (`/mypage/profile/<없는키>` 는 404 = catch-all 아님). 모두 **로그인 필수** 지면이다.
  MyProfileMobile: '/mypage/profile/mobile',
  MyProfilePassword: '/mypage/profile/password',
  MyProfileCustomNumber: '/mypage/profile/custom-number',
  MyProfileRefundBankAccount: '/mypage/profile/refund-bank-account',
  RecentlyViewedGoods: '/mypage/recently-viewed-goods',
  AddressList: '/address',
  Help: '/help',
  // 1:1 문의. 마지막 세그먼트는 문의 sno 또는 신규 작성(`new`).
  HelpInquiry: '/help/inquiry/:inquirySno',
  Coupons: '/coupons',
  Points: '/points',
  Membership: '/membership',
  Notifications: '/notifications',
  Benefits: '/benefits',
  EditGoodsLike: '/like/edit',
  FolderDetail: '/like/folders/:folderSno',
  SearchHelper: '/search/search-helper',
  SearchResult: '/search/search-result',
  Screens: '/screens',
  FragmentScreens: '/screens/fragment',
  ServiceMap: '/overview/icons/all',
} as const;

export type WebRoutePath =
  (typeof WEB_ROUTE_PATHS)[keyof typeof WEB_ROUTE_PATHS];

/**
 * 동적 세그먼트 토큰. `:name` 과 `:name(정규식)` 두 형태를 받는다.
 *
 * `:name(정규식)` 은 react-navigation v7 문법이며 **여러 세그먼트를 한 param 으로 받을 때만**
 * 쓴다(`/payment/success/:slug(.*)` → slug = `m2/ord/complete.php`). react-navigation 은
 * 괄호 안의 `/` 를 세그먼트 구분자로 보지 않는다(`getPatternParts.js` 의 `!isRegex` 가드).
 *
 * 와일드카드(`*`)를 쓰지 않는 이유: react-navigation 이 `*` 를 `.*` 로만 바꾸고 **params 로
 * 캡처하지 않아서**(`getStateFromPath.js` — `it.segment === '*' ? '.*' : ...`) 화면이 나머지
 * 경로를 알 수 없고, 그러면 웹뷰 URL 을 조립할 수 없다. named param + 정규식이면 캡처된다.
 *
 * ⚠️ 정규식 안에 `)` 는 넣을 수 없다(토큰이 먼저 닫힌다). 현재 용도인 `.*` 로 충분하다.
 */
const PATH_PARAM_PATTERN = /:(\w+)(?:\(([^)]*)\))?/g;

const DEFAULT_PARAM_REGEX = '[^/]+';

/** 패턴의 동적 세그먼트 이름들 (선언 순서대로). */
const getPathParamNames = (pattern: string): string[] =>
  [...pattern.matchAll(PATH_PARAM_PATTERN)].map(([, name]) => name);

/** 패턴을 경로 매칭용 정규식으로. 동적 세그먼트는 캡처 그룹이 된다. */
const toRouteRegex = (pattern: string): RegExp =>
  new RegExp(
    '^' +
      pattern.replace(
        PATH_PARAM_PATTERN,
        (_, __, regex: string | undefined) =>
          `(${regex ?? DEFAULT_PARAM_REGEX})`,
      ) +
      '$',
  );

const routeRegexes = Object.values(WEB_ROUTE_PATHS).map(toRouteRegex);

export const isKnownRoute = (path: string): boolean => {
  if (/\.\w+$/.test(path)) {
    return true;
  }
  const normalizedPath =
    path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return routeRegexes.some(regex => regex.test(normalizedPath));
};

export type ScreenName = keyof typeof WEB_ROUTE_PATHS;

/**
 * `ably://webview?url=...` 로 **감싸져** 들어와도 범용 WebViewScreen 이 아니라 전용 화면으로
 * 보낼 경로. 확장할 때 여기에 ScreenName 만 추가하면 된다.
 *
 * WebViewScreen 으로 가면 목적지가 `url` params 문자열로 들어가서 react-navigation 이 만드는
 * 주소가 해당 경로로 유지되지 않는다(뒤에 인코딩된 URL 이 붙는 형태). 전용 화면이 있고
 * Navigator 에 `path` 로 등록돼 있는 경로만 넣는다.
 *
 * (감싸지 않은 내부 경로는 `resolveScreenFromPath` 가 이미 전용 화면으로 보낸다.)
 */
export const WEBVIEW_WRAPPED_NATIVE_SCREENS: ReadonlySet<ScreenName> = new Set([
  'Cart',
  'CartSelectCoupon',
  'PaymentCheckout',
  'PaymentSuccess',
  'PaymentFail',
  'OrderList',
  'OrderStatus',
  'OrderDetail',
  'EventList',
  'EventDetail',
  'EventSegmentDetail',
  'Style',
  'CommunityTalkHome',
  'FunTalk',
  'ChatBot',
  'FortuneHome',
  'LifeFortuneDaily',
  'ContentStyleHome',
  'NovelHome',
  'AffiliateEventDetail',
  'PlaceGoodsDetail',
  'TodayOnboarding',
  'PlaceCategoryList',
  'PlaceMap',
  'PlaceSearch',
  'PlaceReservationList',
  'Help',
  'HelpInquiry',
  'Coupons',
  'Points',
  'Membership',
  'Notifications',
  'Benefits',
  'MarketInfo',
  'GoodsReviewSno',
  'GoodsReviewList',
  'MyProfile',
  'MyProfileMobile',
  'MyProfilePassword',
  'MyProfileCustomNumber',
  'MyProfileRefundBankAccount',
  'MumulLiveProfiles',
  'MumulLiveProfileSno',
  'OrderCancellations',
  'OrderCancellationsDetail',
  'OrderExchange',
  'OrderExchangeSellers',
  'OrderReturn',
  'OrderReturnDetail',
  'OrderSalesInvoice',
  'OrderDeliveryPost',
  'OrderDeliveryPostOrderItem',
  'TodayDelivery',
]);

/**
 * 라우트 패턴의 동적 세그먼트를 채워 path 를 만든다.
 * (`fillWebRoutePath('OrderDetail', { orderSno: 12 })` → `/orders/12`)
 *
 * 전용 웹뷰 화면이 목적지 URL 을 조립할 때 패턴을 다시 적지 않도록 하기 위한 헬퍼.
 */
export const fillWebRoutePath = (
  screen: ScreenName,
  pathParams: Record<string, string | number | undefined>,
): string =>
  WEB_ROUTE_PATHS[screen].replace(
    PATH_PARAM_PATTERN,
    (_, name: string, regex: string | undefined) => {
      const value = pathParams[name];
      if (value == null) return '';
      const raw = String(value);
      // 다단 세그먼트 param(`:slug(.*)`)은 `/` 를 인코딩하면 경로가 깨진다 → 세그먼트별로 인코딩.
      return regex == null
        ? encodeURIComponent(raw)
        : raw.split('/').map(encodeURIComponent).join('/');
    },
  );

/**
 * 웹에서 `ably://webview?url=...` 딥링크를 **새 탭(window.open) 대신 앱 안 `WebViewScreen`
 * (iframe 임베드)으로** 열 경로 prefix.
 *
 * 기본값은 새 탭이다. `window.open` 은 RN 네비게이션 스택을 벗어나 뒤로가기·스택 복귀가
 * 끊기므로, 스택 안에 남아야 하는 경로만 여기에 추가해 임베드로 돌린다.
 *
 * ⚠️ 임베드로 바꾸면 iframe 제약이 따라온다. 추가 전에 대상 페이지에서 확인할 것:
 * - `X-Frame-Options` / CSP `frame-ancestors` 가 없어야 한다(있으면 렌더 자체가 실패).
 * - 페이지가 자체 로그인/결제 리다이렉트로 외부 도메인을 타면 iframe 안에서 막힐 수 있다.
 * - 세로 길이가 유동적인 페이지는 높이를 알 수 없다(cross-origin). WebViewScreen 은
 *   전체 화면을 채우므로 문제되지 않지만, 내부 스크롤이 생긴다.
 * - 페이지가 `window.top` 을 기대하거나 top-level 네비게이션을 하면 부모까지 이동한다.
 */
export const WEB_EMBEDDED_WEBVIEW_PATH_PREFIXES: readonly string[] = [
  // 프레이밍 확인: X-Frame-Options / CSP frame-ancestors 없음(200).
  // 쿠키는 Domain=.a-bly.com 이라 mobile.a-bly.com ↔ m.a-bly.com 이 same-site → 세션 유지됨.
  '/events',
  '/orders',
  '/goods/',
  '/place/goods',
  '/place/list',
  '/payment/checkout',
  '/reviews/',
  '/benefits',
  '/shoplive',
  '/content/',
  '/style',
  '/membership',
  '/youth-policy',
  '/help',
  '/agreement',
  '/oss-licenses',
  '/berry',
  // 프로필 하위 개인정보 수정 화면들(`/mypage/profile/mobile` 등). 전용 화면 4개를 등록해
  // 뒀지만, 그 화면들 안에서 또 다른 `/mypage/profile/...` 로 이동하는 경우까지 앱 안에서
  // 열리도록 prefix 로 묶는다(없으면 웹에서 window.open 새 탭이 된다).
  //
  // ⚠️ prefix 라 **미등록 경로도 앱 안에서 열린다** — 서버에 없는 키는 404 를 앱 안에서
  // 띄운다(`/mypage/profile/<없는키>` 는 404). 새 하위 경로가 생기면 전용 화면도 함께 등록해
  // 주소·타이틀이 유지되게 하는 게 맞다.
  '/mypage/profile',
];

/**
 * 절대 URL 또는 path 가 앱 안 `WebViewScreen` 으로 열 대상인지.
 * (origin 은 무시하고 pathname 만 본다)
 */
export const isEmbeddedWebviewTarget = (urlOrPath: string): boolean => {
  if (WEB_EMBEDDED_WEBVIEW_PATH_PREFIXES.length === 0) return false;

  const pathname = ((): string => {
    try {
      return new URL(urlOrPath).pathname;
    } catch {
      const [rawPath] = urlOrPath.split('?');
      return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    }
  })();

  return WEB_EMBEDDED_WEBVIEW_PATH_PREFIXES.some(prefix =>
    pathname.startsWith(prefix),
  );
};

export type ResolvedRoute = {
  screen: ScreenName;
  /** path의 동적 세그먼트 값 (예: /goods/:goodsId → { goodsId: '123' }) */
  pathParams: Record<string, string>;
  /** 쿼리 파라미터 */
  queryParams: Record<string, string>;
};

/**
 * webLink path에서 screen name과 파라미터를 추출합니다.
 *
 * 예시:
 * - '/goods/123?ref=home'
 *   → { screen: 'GoodsDetail', pathParams: { goodsId: '123' }, queryParams: { ref: 'home' } }
 * - '/' → { screen: 'Home', pathParams: {}, queryParams: {} }
 * - 매칭되는 패턴이 없으면 null
 */
export const resolveScreenFromPath = (path: string): ResolvedRoute | null => {
  const [rawPath, rawQuery = ''] = path.split('?');
  const normalized =
    rawPath.endsWith('/') && rawPath !== '/' ? rawPath.slice(0, -1) : rawPath;

  const queryParams: Record<string, string> = {};
  if (rawQuery) {
    new URLSearchParams(rawQuery).forEach((value, key) => {
      queryParams[key] = value;
    });
  }

  for (const [screenKey, pattern] of Object.entries(WEB_ROUTE_PATHS)) {
    const paramNames = getPathParamNames(pattern);
    const match = normalized.match(toRouteRegex(pattern));
    if (match) {
      const pathParams: Record<string, string> = {};
      paramNames.forEach((name, i) => {
        pathParams[name] = decodeURIComponent(match[i + 1]);
      });
      return {
        screen: screenKey as ScreenName,
        pathParams,
        queryParams,
      };
    }
  }
  return null;
};

/**
 * **절대 URL**(예: `https://m.a-bly.com/cart?from=CARTICON`)을 화면+파라미터로 해석합니다.
 *
 * `resolveScreenFromPath` 의 절대 URL 버전 — origin 을 떼고 path+query 로 위임합니다.
 * `ably://webview?url=...` 처럼 목적지가 URL 로 감싸져 오는 경우에 사용합니다.
 * (url 파라미터가 percent-encoding 된 채 넘어오는 호출부가 있어 한 번 더 디코딩해 시도합니다.)
 */
export const resolveScreenFromUrl = (url: string): ResolvedRoute | null => {
  const parse = (value: string): URL | null => {
    try {
      return new URL(value);
    } catch {
      return null;
    }
  };

  const parsed = parse(url) ?? parse(decodeURIComponent(url));
  if (parsed === null) return null;

  return resolveScreenFromPath(`${parsed.pathname}${parsed.search}`);
};

/** 전용 웹뷰 화면으로 보낼 라우트(화면명 + 그대로 넘길 params). */
export type WebviewWrappedRoute = {
  screen: ScreenName;
  /** path 세그먼트 + 쿼리. 화면이 그대로 웹뷰 URL 로 승계한다. */
  params: Record<string, string>;
};

/**
 * 웹뷰 목적지(절대 URL 또는 path)를 **전용 화면 라우트**로 해석합니다(대상이 아니면 null).
 *
 * 대상은 `WEBVIEW_WRAPPED_NATIVE_SCREENS`. 딥링크 진입(`useWebRoutePush`)과 상품 상세의
 * 리다이렉트(`useCheckGoodsRedirectDeeplink`)가 같은 규칙을 쓰도록 여기 모았습니다.
 */
export const resolveWebviewWrappedRoute = (
  urlOrPath: string,
): WebviewWrappedRoute | null => {
  const resolved =
    resolveScreenFromUrl(urlOrPath) ??
    resolveScreenFromPath(
      urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`,
    );
  if (resolved === null) return null;
  if (!WEBVIEW_WRAPPED_NATIVE_SCREENS.has(resolved.screen)) return null;

  return {
    screen: resolved.screen,
    params: { ...resolved.pathParams, ...resolved.queryParams },
  };
};

/**
 * `ResolvedRoute` 를 다시 path+query 문자열로 만듭니다(웹으로 내보낼 URL 조립용).
 */
export const buildPathFromResolvedRoute = (resolved: ResolvedRoute): string => {
  const pattern: string = WEB_ROUTE_PATHS[resolved.screen];
  const path = pattern.replace(
    /:(\w+)/g,
    (_, name: string) => resolved.pathParams[name] ?? '',
  );
  const query = new URLSearchParams(resolved.queryParams).toString();
  return query.length > 0 ? `${path}?${query}` : path;
};
